import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Bell, Mail } from 'lucide-react';
import type {
  NotificationSubscriptionDomainNode,
  NotificationSubscriptionEventNode,
} from '../../lib/api';
import type { SubscriptionChannel } from './useSubscriptionTree';

interface SubscriptionDomainRowProps {
  domain: NotificationSubscriptionDomainNode;
  /** False when the tenant's master email switch is off. */
  emailsEnabled: boolean;
  saving: boolean;
  onMute: (
    scopeType: 'domain',
    scopeKey: string,
    channel: SubscriptionChannel,
    muted: boolean,
  ) => void;
}

/** The effective state of one channel on an event, with a safe default. */
function channelState(
  event: NotificationSubscriptionEventNode,
  channel: SubscriptionChannel,
) {
  return event.effective?.[channel] ?? { muted: false, legacy_blocked: false };
}

/**
 * Is a whole domain muted on this channel?
 *
 * Domain nodes carry only the user's own row, not a resolved state, so the
 * answer is read from the events beneath them: those *are* resolved, and a
 * domain-level mute is exactly what makes every one of them muted. Reading the
 * row alone would show nothing for a mute inherited from the global scope.
 */
function domainMuted(
  events: NotificationSubscriptionEventNode[],
  channel: SubscriptionChannel,
) {
  const live = events.filter(event => event.tenant_enabled);
  if (live.length === 0) return false;
  return live.every(event => channelState(event, channel).muted);
}

/**
 * CR-7110: does an old per-category opt-out still hold back part of a domain
 * on this channel? Those flags stop a send whatever the section row says, so
 * the switch must not read as on while one does. Switching on clears them.
 */
function domainHeld(
  events: NotificationSubscriptionEventNode[],
  channel: SubscriptionChannel,
) {
  return events.some(event =>
    event.tenant_enabled && channelState(event, channel).legacy_blocked);
}

/** What the person is told when an old opt-out holds a channel back. */
const HELD_NOTES: Record<SubscriptionChannel, string> = {
  email: 'Some emails in this section are off from an earlier setting. '
    + 'Switch on to receive all of them.',
  bell: 'Some in-app notifications in this section are off from an earlier '
    + 'setting. Switch on to receive all of them.',
};

/**
 * One section of a person's notification settings.
 *
 * On or off, per section, and nothing else. Which events exist, on which
 * channels and how often they send is the site's decision in Admin Setup —
 * putting any of that in front of every user is asking them to re-make an
 * administrator's call. The only question left for a person is whether they
 * want a section at all.
 */
export function SubscriptionDomainRow({
  domain, emailsEnabled, saving, onMute,
}: SubscriptionDomainRowProps) {
  const events: NotificationSubscriptionEventNode[] =
    domain.subdomains.flatMap(sub => sub.events);
  const bellMuted = domainMuted(events, 'bell');
  const emailMuted = domainMuted(events, 'email');
  // A section the person switched off themselves needs no explanation. With
  // the tenant's email off the column is inactive and says why already.
  const bellHeld = !bellMuted && domainHeld(events, 'bell');
  const emailHeld = emailsEnabled && !emailMuted && domainHeld(events, 'email');
  const noteId = (channel: SubscriptionChannel) =>
    `${domain.scope_key}-${channel}-held`;

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border p-4">
      <div className="flex flex-1 items-center gap-2">
        <span className="font-medium">{domain.label}</span>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">In-app</Label>
          <Switch
            checked={!bellMuted && !bellHeld}
            disabled={saving}
            onCheckedChange={next =>
              onMute('domain', domain.scope_key, 'bell', !next)}
            aria-label={`In-app notifications for ${domain.label}`}
            aria-describedby={bellHeld ? noteId('bell') : undefined}
          />
        </div>

        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">Email</Label>
          <Switch
            checked={emailsEnabled && !emailMuted && !emailHeld}
            // A tenant that has turned email off entirely is not something this
            // switch can override, so it reads as off and says why rather than
            // pretending to work.
            disabled={saving || !emailsEnabled}
            onCheckedChange={next =>
              onMute('domain', domain.scope_key, 'email', !next)}
            aria-label={`Email notifications for ${domain.label}`}
            aria-describedby={emailHeld ? noteId('email') : undefined}
          />
        </div>
      </div>

      {(['bell', 'email'] as const)
        .filter(channel => (channel === 'bell' ? bellHeld : emailHeld))
        .map(channel => (
          <p
            key={channel}
            id={noteId(channel)}
            className="w-full text-sm text-muted-foreground"
          >
            {HELD_NOTES[channel]}
          </p>
        ))}
    </div>
  );
}
